import { useRouter, useNavigate, createFileRoute } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { postLogout } from "@/lib/api/auth";
import { apiClient } from "@/lib/api/client";
import { useEffect, useState } from "react";
import { Spinner } from "@/components/ui/spinner";

interface UserProfile {
  userId: string;
  name: string;
  isApproved: boolean;
  role: string;
}

function PendingApprovalPage() {
  const router = useRouter();
  const navigate = useNavigate();
  const [user, setUser] = useState<UserProfile | null>(null);

  useEffect(() => {
    const loadUserData = async () => {
      try {
        // 최신 사용자 정보 가져오기
        const response = await apiClient.get<UserProfile>("/auth/me");
        const userData = response.data;

        // localStorage 업데이트
        localStorage.setItem("growing-teacher-user", JSON.stringify(userData));

        // 승인되었으면 메인 페이지로 리다이렉트
        if (userData.isApproved) {
          navigate({ to: "/main", replace: true });
          return;
        }

        setUser(userData);
      } catch (error) {
        console.error("사용자 정보 로드 실패:", error);
        // 인증 실패 시 로그아웃
        await postLogout();
        localStorage.removeItem("growing-teacher-user");
        navigate({ to: "/auth/login", replace: true });
      }
    };

    loadUserData();
  }, [router]);

  const handleLogout = async () => {
    try {
      await postLogout();
      localStorage.removeItem("user");
      navigate({ to: "/" });
    } catch (error) {
      console.error("로그아웃 실패:", error);
      localStorage.removeItem("user");
      navigate({ to: "/" });
    }
  };

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner className="size-8" />
      </div>
    );
  }

  return (
    <main className="min-h-screen flex items-center justify-center p-6">
      <Card className="max-w-md w-full">
        <CardContent className="pt-6 text-center">
          <div className="mb-6">
            <div className="w-20 h-20 mx-auto mb-4 bg-orange-100 rounded-full flex items-center justify-center">
              <span className="text-4xl">⏳</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              관리자 승인 대기 중
            </h2>
            <p className="text-gray-600 mb-4">
              선생님 계정 등록이 완료되었습니다.
            </p>
            <p className="text-sm text-gray-500 leading-relaxed">
              관리자의 승인이 완료되면 학생 관리 기능을 이용하실 수 있습니다.
              <br />
              승인은 보통{" "}
              <span className="font-semibold text-orange-600">1-2일</span> 내에
              완료됩니다.
            </p>
          </div>

          <div className="space-y-3 pt-4 border-t">
            <div className="text-sm text-gray-500">
              <p className="mb-1">가입 정보</p>
              <p className="font-medium text-gray-900">
                {user.name} ({user.userId})
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {user.role === "teacher" ? "선생님" : "관리자"}
              </p>
            </div>

            <Button onClick={handleLogout} variant="outline" className="w-full">
              로그아웃
            </Button>
          </div>
        </CardContent>
      </Card>
    </main>
  );
}

export const Route = createFileRoute("/pending-approval")({
  component: PendingApprovalPage,
});
